import { useTranslationAdminDashboard } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { Flex } from '@mantine/core';
import { SummaryItem } from '@dealstack/frontend/shared/components/SummaryItem/SummaryItem';
import { DealUsers } from '@dealstack/frontend/app/pages/Project/DealDetails/DealUsers';
import { ContainerWithHeader } from '@dealstack/frontend/shared/components/ContainerWithHeader/ContainerWithHeader';
import { BasicUserWithPermissionRolesRes } from '@dealstack/shared/lib/generated';
import { generatePath, Link } from 'react-router-dom';
import { DEAL_ROUTES, ROUTES } from '@dealstack/frontend/app/pages/Routing/routes.ts';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds.ts';
import { SummaryWrapper } from '@dealstack/frontend/app/pages/Project/DealDetails/styled.ts';
import {
	PermissionRole,
	ResourceType,
} from '@dealstack/shared/lib/common/fineGrainedAuthorization/permissions.ts';
import { defaultToEmptyString } from '@dealstack/shared/lib/format/string.ts';
import { PermissionWrapper } from '@dealstack/frontend/shared/components/PermissionWrapper/PermissionWrapper.tsx';
import { CURRENCY_SYMBOLS, formatCurrency } from '@dealstack/shared/lib/format/currency/currency';

interface Props {
	targetName?: string;
	currency?: string;
	users: BasicUserWithPermissionRolesRes[];
	onAddMemberClick: () => void;
	dealId?: string;
}

export const DealSummary = ({ targetName, currency, users, onAddMemberClick, dealId }: Props) => {
	const tAdminDashboard = useTranslationAdminDashboard();

	const settingsAction = dealId && (
		<PermissionWrapper
			resourceType={ResourceType.DEAL}
			resourceId={defaultToEmptyString(dealId)}
			permissionRole={PermissionRole.VIEWER}
		>
			<Link to={generatePath(`${ROUTES.DEAL}${DEAL_ROUTES.SETTINGS}`, { dealId })}>
				<Flex align="center" justify="center">
					<Icon
						variant="gearv2"
						size="vlg"
						data-testid={TEST_ELEMENT_IDS.ADMIN_DEAL_PAGE_SETTINGS_TILE}
					/>
				</Flex>
			</Link>
		</PermissionWrapper>
	);

	return (
		<SummaryWrapper>
			<ContainerWithHeader
				title={tAdminDashboard('deal.sidebar.about_label')}
				headerActions={settingsAction}
			>
				<Flex direction="column" gap="0.5rem">
					<SummaryItem
						title={tAdminDashboard('create_project.target_field.label')}
						value={targetName}
						item="target-name"
					/>
					<SummaryItem
						title={tAdminDashboard('create_project.currency_field.label')}
						value={formatCurrency(currency as keyof typeof CURRENCY_SYMBOLS)}
						item="currency"
					/>
					<SummaryItem
						title={tAdminDashboard('create_project.deal_field.label')}
						item="deal-users"
					>
						<Flex gap="0.25rem" wrap="wrap">
							<DealUsers
								users={users}
								onAddClick={onAddMemberClick}
								dealId={dealId ?? ''}
							/>
						</Flex>
					</SummaryItem>
				</Flex>
			</ContainerWithHeader>
		</SummaryWrapper>
	);
};
